import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import type { Pc } from "@/types/pc";
import type { CurrentUser, User } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ReservationModal } from "@/components/ReservationModal";
import { ReservationCard } from "@/components/ReservationCard";

interface ApproverRow {
  id: number;
  display_name: string;
  email: string;
  active: number;
  receive_line_notifications: number;
}

export function ApprovalManagementPage({ currentUser }: { currentUser: CurrentUser }) {
  const [pending, setPending] = useState<Reservation[]>([]);
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [approvers, setApprovers] = useState<ApproverRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Reservation | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Reservation[]>("/approvals"),
      api.get<Pc[]>("/pcs"),
      api.get<User[]>("/users"),
      api.get<ApproverRow[]>("/approvals/approvers"),
    ])
      .then(([p, pc, u, a]) => {
        setPending(p);
        setPcs(pc);
        setUsers(u);
        setApprovers(a);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pcName = useMemo(() => {
    const map = new Map(pcs.map((p) => [p.id, p.name]));
    return (id: number) => map.get(id) ?? `PC#${id}`;
  }, [pcs]);
  const userName = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u.displayName]));
    return (id: number) => map.get(id) ?? `利用者#${id}`;
  }, [users]);

  const toggleApprover = async (userId: number, active: boolean) => {
    await api.post(`/approvals/approvers/${userId}`, { active });
    load();
  };

  if (loading) return <LoadingState />;
  if (error) return <EmptyState message={error} />;

  return (
    <div>
      <h1 className="page-title">承認管理</h1>
      <p className="page-subtitle">利用申請の承認・却下と承認者の設定を行います。</p>

      <div className="two-col">
        <div className="card card-padded">
          <h2 className="section-title">承認待ちの申請</h2>
          {pending.length === 0 ? (
            <EmptyState message="承認待ちの申請はありません。" />
          ) : (
            pending.map((r) => (
              <ReservationCard
                key={r.id}
                reservation={r}
                pcName={pcName(r.pc_id)}
                userName={userName(r.user_id)}
                onClick={() => setSelected(r)}
              />
            ))
          )}
        </div>

        {currentUser.role === "admin" && (
          <div className="card card-padded">
            <h2 className="section-title">承認者設定</h2>
            <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: -8 }}>
              LINE通知を受け取る承認者を選択します。
            </p>
            {users
              .filter((u) => u.role === "manager" || u.role === "admin")
              .map((u) => {
                const current = approvers.find((a) => a.id === u.id);
                const isActive = Boolean(current?.active);
                return (
                  <label
                    key={u.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 0",
                      borderBottom: "1px solid var(--color-border)",
                      fontSize: 14,
                    }}
                  >
                    <input type="checkbox" checked={isActive} onChange={(e) => toggleApprover(u.id, e.target.checked)} />
                    {u.displayName}（{u.email}）
                  </label>
                );
              })}
          </div>
        )}
      </div>

      {selected && (
        <ReservationModal
          reservation={selected}
          pcName={pcName(selected.pc_id)}
          userName={userName(selected.user_id)}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
